/**
 * Hook لقراءة وسائط الهيرو من السيرفر مع cache-bust آليّ.
 *
 * أولويّة المصادر (للسوق الفعّال للزائر):
 *  1) cache محليّ من زيارة سابقة (يُعرض فوراً — لا وميض للـposter القديم).
 *  2) حملة فعّالة الآن في `hero_campaigns`.
 *  3) إعدادات app_settings القديمة.
 *  4) لا fallback محزَّم — خلفيّة سوداء (--onyx) في HeroMedia عند الغياب.
 */
import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getHeroMediaUrls } from "@/lib/admin/heroMedia.functions";
import { getActiveHeroCampaign } from "@/lib/admin/heroCampaigns.functions";
import { useLocale } from "@/contexts/LocaleContext";
import { useIsMobile } from "@/hooks/use-mobile";

export type HeroCampaignText = {
  title_ar: string | null;
  title_en: string | null;
  subtitle_ar: string | null;
  subtitle_en: string | null;
  cta_label_ar: string | null;
  cta_label_en: string | null;
  cta_href: string | null;
  scroll_cta_label_ar: string | null;
  scroll_cta_label_en: string | null;
  scroll_cta_href: string | null;
  scroll_cta_enabled: boolean | null;
  text_position: import("@/lib/admin/heroCampaigns.shared").HeroTextPosition;
};
type HeroUrls = { desktop: string; mobile: string; poster: string; campaignId: string | null; text: HeroCampaignText | null; heightMode: import("@/lib/admin/heroCampaigns.shared").HeroHeightMode };
// v7: إبطال الأغلفة القديمة المحذوفة؛ لا يجوز عرض cache poster سابق قبل ردّ الخادم.
const CACHE_PREFIX = "lecmo:hero-urls:v7:";


function readCache(market: string): HeroUrls | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + market);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<HeroUrls>;
    if (parsed && (parsed.desktop || parsed.mobile || parsed.poster))
      return {
        desktop: parsed.desktop ?? "",
        mobile: parsed.mobile ?? "",
        poster: parsed.poster ?? "",
        campaignId: parsed.campaignId ?? null,
        text: parsed.text ?? null,
        heightMode: parsed.heightMode ?? "full",
      };
  } catch {
    /* ignore */
  }
  return undefined;
}

function writeCache(market: string, urls: HeroUrls) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + market, JSON.stringify(urls));
  } catch {
    /* ignore */
  }
}

export function useHeroVideoUrls(page: string = "home") {
  const fnLegacy = useServerFn(getHeroMediaUrls);
  const fnCampaign = useServerFn(getActiveHeroCampaign);
  const { effectiveMarket } = useLocale();
  const isMobile = useIsMobile();
  const device: "mobile" | "desktop" = isMobile ? "mobile" : "desktop";
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const cacheKey = `${page}:${effectiveMarket}:${device}`;

  const q = useQuery({
    queryKey: ["hero-media-urls", page, effectiveMarket, device],
    queryFn: async (): Promise<HeroUrls> => {
      const campaign = await fnCampaign({
        data: { market: effectiveMarket, page, device },
      });
      let result: HeroUrls;
      if (campaign && (campaign.desktop || campaign.mobile || campaign.poster)) {
        // لا خلط بين المقاسين: كلّ مقاس يحتفظ بوسيطه (فيديو أو صورة) كما هو،
        // وHeroMedia يميّز النوع من الامتداد. هكذا لا يتسرّب فيديو الموبايل
        // إلى الديسكتوب عندما يضع الأدمن صورة للديسكتوب.
        const dRaw = campaign.desktop || "";
        const mRaw = campaign.mobile || "";
        // ممنوع استخدام صورة مقاس آخر كـposter — كانت صورة الديسكتوب تظهر
        // لحظة قبل فيديو الموبايل. الـposter = poster الحملة فقط.
        // لكن إن كان أحد المقاسين فارغاً تماماً نملأه بصورة (لا فيديو) من الحملة
        // نفسها، حتى لا تسقط الصفحة إلى صورة الهوم/الافتراضيّة.
        const isImg = (u: string) => !!u && !/\.(mp4|webm|mov|m4v)(\?|$)/i.test(u);
        const fillImg = (other: string) =>
          isImg(campaign.poster || "") ? campaign.poster! : isImg(other) ? other : "";
        result = {
          desktop: dRaw || fillImg(mRaw),
          mobile: mRaw || fillImg(dRaw),
          poster: campaign.poster || "",

          campaignId: campaign.id,
          heightMode: campaign.height_mode ?? "full",
          text: {
            title_ar: campaign.title_ar ?? null,
            title_en: campaign.title_en ?? null,
            subtitle_ar: campaign.subtitle_ar ?? null,
            subtitle_en: campaign.subtitle_en ?? null,
            cta_label_ar: campaign.cta_label_ar ?? null,
            cta_label_en: campaign.cta_label_en ?? null,
            cta_href: campaign.cta_href ?? null,
            scroll_cta_label_ar: campaign.scroll_cta_label_ar ?? null,
            scroll_cta_label_en: campaign.scroll_cta_label_en ?? null,
            scroll_cta_href: campaign.scroll_cta_href ?? null,
            scroll_cta_enabled: campaign.scroll_cta_enabled ?? null,
            text_position: campaign.text_position ?? "bottom-left",
          },
        };
      } else if (page === "home") {
        const legacy = await fnLegacy({ data: { market: effectiveMarket } });
        result = { desktop: legacy.desktop, mobile: legacy.mobile, poster: legacy.poster, campaignId: null, text: null, heightMode: "full" };
      } else {
        // صفحات داخليّة بلا حملة نشطة: لا fallback إلى app_settings القديمة (خاصّة بالرئيسيّة).
        result = { desktop: "", mobile: "", poster: "", campaignId: null, text: null, heightMode: "full" };
      }
      writeCache(cacheKey, result);
      return result;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
    initialData: mounted ? () => readCache(cacheKey) : undefined,
    initialDataUpdatedAt: 0,
  });

  return {
    desktop: q.data?.desktop ?? "",
    mobile: q.data?.mobile ?? "",
    poster: q.data?.poster ?? "",
    campaignId: q.data?.campaignId ?? null,
    text: q.data?.text ?? null,
    heightMode: q.data?.heightMode ?? "full",
    isLoading: q.isLoading,
  };
}

